import { redirect, redirectDocument } from "react-router";
import { setTokenProvider } from "../utils/auth";
import { setAuthPortalRedirectUrl } from "../utils/api";
import {
  callAuthExchange,
  callPatchCurrentTenant,
  callWhoami,
} from "../utils/auth-api";
import { parseHashParams } from "../utils/url";
import { resolveLocalViteSignInUrl } from "../utils/localVite";
import { authPortalTenantPath, tenantIdFromPathname } from "../utils/tenant";
import {
  clearReturnTo,
  isSafeReturnPath,
  pathWithoutQuery,
  PORTAL_RESUME_PARAMS,
  returnToKey,
  saveReturnTo,
  takeReturnTo,
  toReturnPath,
} from "../utils/returnTo";
import { lastTenantKey, readLastTenant, saveLastTenant } from "../utils/lastTenant";
import type { WhoamiMe } from "../../shared-domain/auth-service/contract";

export interface AuthLoaderConfig {
  /** API エンドポイント */
  apiEndpoint: string;
  /** 認証ポータル URL */
  authPortalUrl: string;
  /** プロダクト ID */
  productId: string;
  /** トークンストア名（sessionStorage のキープレフィックス） */
  tokenStoreName: string;
  /** Cognito User Pool ID */
  userPoolId: string;
  /** Cognito Client ID */
  clientId: string;
  /** リフレッシュトークンAPIのURL */
  authRefreshUrl: string;
  /**
   * PRODUCT_DEV-641: 配信ホストだけでテナントが決まる環境の tenantId。
   *
   * **optional。** 無印や dev-lab / admin / survey は渡さないので、
   * 未指定のときの挙動は従来と完全に同じ。
   */
  fixedTenantId?: string;
  /**
   * PRODUCT_DEV-916: ログイン後に、開こうとしていた画面へ戻す。
   *
   * 認証ポータルは戻り先を覚えず、ログイン後は常に productUrl（ルート）へ戻す。
   * 有効にすると、ポータルへ出す直前に今の URL を退避し、`#authCode` で戻ってきた
   * 直後に復元する（仕組みと罠は `utils/returnTo.ts`）。
   *
   * **optional。** 渡さないプロダクトの挙動は従来と完全に同じ。dev-lab は
   * root-loader で自前の退避を持っている（PRODUCT_DEV-548）ので、ここを有効にするなら
   * そちらを外すこと（両方あると退避キーが2つになる）。
   *
   * ⚠ 退避値は `request.url` の pathname そのもの（BASE_URL 込み）。Unizo は router に
   * basename を渡さない方式なのでそのまま戻せるが、**`basename` を渡すプロダクト
   * （admin / survey / dev-lab のローカル `/vite/<product>`）で有効にすると、redirect で
   * basename が二重に付く可能性がある（未検証）**。有効にするときにローカルで確かめること。
   *
   * PRODUCT_DEV-1129: 退避に加えて、戻り先をポータルへの URL の `returnTo` にも載せる。
   * ポータルはそれを `#authCode=…&returnTo=…` で返すので、ログイン画面で長く置かれて
   * 退避（15 分）が切れても戻れる。退避は、ポータルが返さないとき（反映前など）の予備。
   */
  restoreReturnTo?: boolean;
  /**
   * PRODUCT_DEV-1129: 直前に使っていたテナントを覚え、ポータルへの URL の
   * `lastTenantId` に載せる（再ログインでテナントを選び直させないため。仕組みは
   * `utils/lastTenant.ts`）。**optional。** 渡さないプロダクトの挙動は従来と同じ。
   */
  rememberLastTenant?: boolean;
}

/**
 * 認証 loader を作成するヘルパー関数
 * @param config 認証設定
 * @returns loader 関数
 */
export function createAuthLoader(config: AuthLoaderConfig) {
  return async ({ request }: { request: Request }): Promise<WhoamiMe> => {
    const {
      apiEndpoint,
      authPortalUrl,
      productId,
      tokenStoreName,
      userPoolId,
      clientId,
      authRefreshUrl,
      fixedTenantId,
      restoreReturnTo,
      rememberLastTenant,
    } = config;

    // 認証ポータルの URL
    const params = new URLSearchParams();
    if (productId) {
      params.append("productId", productId);
    }

    // PRODUCT_DEV-421: 現在の URL の先頭セグメントがテナントID（スラッグ形式）であれば
    // 認証ポータルのパスにも `/{tenantId}/` を付与し、エイリアスログインモードを発動させる。
    // 例: unizo.umeee-app.com/umeee-3jz2/... → auth.umeee-app.com/umeee-3jz2/?productId=unizo
    //
    // PRODUCT_DEV-641: 判定の正本は `utils/tenant.ts`（同じ正規表現が unizo の
    // root-loader にも重複していたため1本に寄せた）。配信ホストでテナントが決まる
    // 環境では常に `/` を返す（認証ポータルも同じ環境のビルドなので自分が誰のポータルか
    // 知っている。URL に載せると正本が2つになる）。
    const requestUrl = new URL(request.url);
    const tenantPath = authPortalTenantPath({
      pathname: requestUrl.pathname,
      fixedTenantId,
    });
    const baseAuthUrl = authPortalUrl.replace(/\/+$/, "");

    // PRODUCT_DEV-1129: 直前のテナント。固定テナントの環境ではポータルも自分のテナントを
    // 知っているので載せない。**ここで読んだ値（今回の認証より前に使っていたテナント）を
    // 下の「違うテナントで入ったら戻さない」の判定にも使う。**
    const lastTenantStoreKey =
      rememberLastTenant && !fixedTenantId
        ? lastTenantKey(tokenStoreName)
        : null;
    const previousTenantId = lastTenantStoreKey
      ? readLastTenant(lastTenantStoreKey)
      : null;
    // テナント付きの URL（`/<tenantId>/...`。共有された商談の URL など）から出るときは、
    // 覚えていたテナントより **URL のテナントを優先する**。覚えていた方で入ると、
    // 別のテナントのまま共有された画面へ移ってしまう。
    const urlTenantId =
      lastTenantStoreKey && tenantPath !== "/"
        ? tenantIdFromPathname(requestUrl.pathname)
        : null;

    /**
     * ポータルへ出す URL。`returnTo` と `lastTenantId` は呼んだ時点の値で作る
     * （401 の経路は loader が走ってからしばらく経って呼ばれ、その間に画面もテナントも変わりうる）。
     * `returnTo` は**パスだけ**載せる（クエリには検索語などが入り、ポータルのログや閲覧履歴に
     * 残るため。クエリは同じオリジンの退避から戻す）。
     */
    const reAuthUrlFor = (returnPath: string | null = null) => {
      const query = new URLSearchParams(params);
      const lastTenantId =
        urlTenantId ??
        (lastTenantStoreKey ? readLastTenant(lastTenantStoreKey) : null);
      if (lastTenantId) query.set(PORTAL_RESUME_PARAMS.lastTenantId, lastTenantId);
      const pathOnly = returnPath ? pathWithoutQuery(returnPath) : null;
      if (restoreReturnTo && pathOnly) query.set(PORTAL_RESUME_PARAMS.returnTo, pathOnly);
      return `${baseAuthUrl}${tenantPath}?${query.toString()}`;
    };

    // PRODUCT_DEV-814: ローカル開発ノード(/vite/<product>/ 配下)では認証ポータルへ出さない。
    // 出すと Portal が productId で 1 件に絞って自動リダイレクトし、戻り先が DDB Products の
    // productUrl（= dev 環境の URL）になるため、ローカルのリンクを踏んだのに dev 環境が開く。
    // 同じノードの /vite/ 一覧（Portal ログイン導線あり）へ戻す。
    const localSignInUrl = resolveLocalViteSignInUrl(
      window.location.pathname,
      window.location.origin,
    );
    // ローカルでは `&reauth` / `&auto` の制御は意味を持たない（戻り先が静的ページのため）。
    const reauthTarget = (returnPath: string | null = null) =>
      localSignInUrl ?? reAuthUrlFor(returnPath);

    // URL hash をパース（退避の判定で使うので、ポータルへ出す処理より前に読む）
    const hash = parseHashParams(window.location.hash);
    // PRODUCT_DEV-1129: ポータルが返した戻り先。書き換えられうる入力なので退避と同じ検査を通す。
    const portalReturnTo = hash[PORTAL_RESUME_PARAMS.returnTo];
    const returnToFromPortal =
      typeof portalReturnTo === "string" && isSafeReturnPath(portalReturnTo)
        ? portalReturnTo
        : null;

    // PRODUCT_DEV-916: 開こうとしていた画面の退避。無効なプロダクトでは何もしない。
    const returnToStoreKey = restoreReturnTo
      ? returnToKey(tokenStoreName)
      : null;

    /**
     * 未認証時のリダイレクトを投げる。
     *
     * ⚠ ローカルの戻り先は **同一オリジン** なので、素の `redirect()` だと react-router が
     * basename と突き合わせてクライアント遷移に潰すことがある
     * （`stripBasename(pathname, basename) != null` なら相対化される）。
     * dev-lab / admin は `basename="/vite/<product>"` なので `/vite/` は basename 外＝
     * ドキュメント遷移になるが、**unizo は createBrowserRouter に basename を渡しておらず**
     * （ルート route の `path` に BASE_URL を入れる方式）basename が "/" になるため、
     * `/vite/` へクライアント遷移し、どのルートにも一致せず SPA の 404 で行き止まりになる。
     * `redirectDocument` はその判定より先に効くので、router の設定に依存しなくなる。
     */
    // ⚠ 型注釈を変数側に置くこと。TS が「この呼び出しから先へは進まない」と解釈して
    // 後続を到達不能と扱うのは、明示的に `never` 型と注釈された識別子だけ。
    // アロー関数の戻り値注釈だけだと narrowing されず、`whoamiResponse.me` 等が
    // undefined 込みの型のまま残ってコンパイルが通らない。
    const throwReauth: (suffix?: string) => never = (suffix = "") => {
      // PRODUCT_DEV-916: 開こうとした画面を退避する。
      // - `#authCode` で戻ってきた直後の失敗では触らない。ポータルは常にルートへ戻すので、
      //   ここで書き換えると最初に開こうとした画面を失う
      // - 退避しない画面（トップ・テナントの入口・エラーページ）を開いたときは**前の値を消す**。
      //   残すと「商談詳細でポータルに出る → ログインせずトップを開き直す → ログイン」で
      //   トップではなく商談詳細に着く
      //
      // PRODUCT_DEV-1129: `#authCode` で戻った直後の失敗では、ポータルから受け取った戻り先を
      // 次の往復へ引き継ぐ（退避は 15 分で切れるので、URL に載せないとまた戻れなくなる）。
      let returnPath: string | null = null;
      if (returnToStoreKey && !hash.authCode) {
        returnPath = toReturnPath(requestUrl.pathname, requestUrl.search);
        if (returnPath) saveReturnTo(returnToStoreKey, returnPath);
        else clearReturnTo(returnToStoreKey);
      } else if (returnToStoreKey) {
        returnPath = returnToFromPortal;
      }
      // URL のテナントで入るよう勧めたので、戻ってきたときの「違うテナントか」もそれと比べる
      if (lastTenantStoreKey && urlTenantId) {
        saveLastTenant(lastTenantStoreKey, urlTenantId);
      }
      if (localSignInUrl) throw redirectDocument(localSignInUrl);
      throw redirect(`${reAuthUrlFor(returnPath)}${suffix}`);
    };

    // PRODUCT_DEV-549: 401が強制リフレッシュ後も解消しない場合に shared-ui api.ts が
    // 遷移する認証ポータルURLを登録する（データAPIの永続401からの再ログイン導線）。
    // ここもローカルでは /vite/ 一覧にしないと、セッション途中の永続 401 から
    // 同じく dev 環境へ吸い込まれる。
    //
    // PRODUCT_DEV-916: 401 は画面を開いてしばらく経ってから起きるので、退避するのは
    // loader が走ったときの URL ではなく**ポータルへ飛ぶ瞬間に表示していた画面**。
    // - 画面を移る途中の loader で 401 になった場合は、移る前の画面になる
    //   （react-router は loader が終わるまでアドレスバーを更新しない）
    // - こちらでは退避しない画面でも前の値を**消さない**。`#authCode` でトップに戻った
    //   直後に子ルートが 401 を受けると、交換が終わる前に最初の退避を失うため
    //
    // PRODUCT_DEV-1129: 戻り先と直前のテナントを URL にも載せるので、URL は飛ぶ瞬間に作る。
    // どちらも載せないプロダクトは従来どおり文字列で渡す。
    const currentReturnPath = () =>
      toReturnPath(window.location.pathname, window.location.search);
    setAuthPortalRedirectUrl(
      returnToStoreKey || lastTenantStoreKey
        ? () => reauthTarget(returnToStoreKey ? currentReturnPath() : null)
        : reauthTarget(),
      returnToStoreKey
        ? () => saveReturnTo(returnToStoreKey, currentReturnPath())
        : undefined,
    );

    // Amplify の TokenProvider を初期化
    setTokenProvider(tokenStoreName, userPoolId, clientId, authRefreshUrl);

    // authCode がある場合は hash を削除（履歴に残さない）
    const url = new URL(request.url);
    if (hash.authCode) {
      const cleanUrl = `${url.pathname}${url.search}`;
      window.history.replaceState(null, "", cleanUrl);
    }

    try {
      // authCode がある場合はトークン交換
      let exchangeTenantId = null;
      if (hash.authCode) {
        const exchangeResponse = await callAuthExchange(
          hash.authCode,
          apiEndpoint,
          tokenStoreName,
        );

        if (!exchangeResponse.success) {
          console.error("Token exchange failed");
          throwReauth();
        }
        // ログイン画面で指定したテナントを初期表示する
        exchangeTenantId = exchangeResponse.body.tenantId;
      }

      // whoami で認証確認
      const whoamiResponse = await callWhoami(apiEndpoint);

      if (!whoamiResponse.success) {
        console.error("Authentication failed");
        // 自動ログインの無限リダイレクト防止
        // PRODUCT_DEV-549: `&logout`(Portal が global signOut=共有 refreshToken を revoke)ではなく
        // `&reauth`(Portal は revoke せず手動ログイン表示)に変更。auto 再試行が失敗した時のみ到達。
        if (hash.auto != null) {
          throwReauth("&reauth");
        }
        throwReauth();
      }

      // PRODUCT_DEV-641: 配信ホストでテナントが決まる環境では、そのテナントに
      // 所属していないユーザをここで止める。**通しても各 API が 403 になるだけ**で、
      // 画面はエラーだらけの状態で描画されてしまう。
      //
      // ⚠ これは認可ではなく導線の話。認可の最終防衛線は従来どおりサーバ側の
      // `authMiddleware`（UserTenants との突合）で、そちらは何も変えていない。
      if (
        fixedTenantId &&
        !whoamiResponse.me.memberships.some((m) => m.tenantId === fixedTenantId)
      ) {
        console.error("Not a member of the fixed tenant");
        throwReauth("&reauth");
      }

      // ログイン画面で指定したテナント (exchangeTenantId) がなければ
      // 最近表示したテナント (prevTenantId) を初期表示する
      //
      // PRODUCT_DEV-641: 固定テナントは**環境の不変条件**なので最優先。
      // ここを exchangeTenantId より後ろにすると、別テナントで発行された
      // ワンタイムコードを持ち込まれたときに固定が破れる。
      const prevTenantId = whoamiResponse.me.currentTenantId;
      const tenantId = fixedTenantId || exchangeTenantId || prevTenantId;

      // テナントIDが取得できない場合は再ログインを促す
      // PRODUCT_DEV-549: revoke を誘発しない `&reauth` を使用（理由は上記コメント参照）。
      if (!tenantId) {
        throwReauth("&reauth");
      }

      // ログイン時（authCode交換あり）は常にlastLoginDateを更新する
      // テナント変更がない場合でもPATCHを呼ぶことでproductsInfo.lastLoginDateが更新される
      // ページリフレッシュ時（authCodeなし）はAPI呼び出しを省略する
      if (tenantId !== prevTenantId || exchangeTenantId) {
        await callPatchCurrentTenant(apiEndpoint, tenantId);
      }

      // PRODUCT_DEV-916: 退避した画面へ戻す。
      // **戻すのは `#authCode` で戻ってきた直後だけ、退避は毎回消す**（PRODUCT_DEV-565 と同じ）。
      // 往復が終わらずに残った値でリロード時に飛ばすと、今いる画面から追い出してしまう。
      // 今の画面と同じなら移り直さない（ローカルの /vite/ 一覧は next で元の画面へ戻すため）。
      //
      // PRODUCT_DEV-1129: ポータルが `#…&returnTo=` で返した戻り先を先に使い、無ければ退避を使う
      // （ポータルの反映前・`returnTo` を載せない経路の予備）。ポータルから来た値も
      // 書き換えられうる入力なので、退避と同じ検査を通す。
      // **覚えていたテナントと違うテナントで入ったら戻さない。** 前に使っていた人・前の
      // テナントの画面なので、開いても見られないか、別の人の画面になる。
      if (lastTenantStoreKey) saveLastTenant(lastTenantStoreKey, tenantId);
      if (returnToStoreKey) {
        const saved = takeReturnTo(returnToStoreKey);
        // ポータルが返すのはパスだけ。同じ画面の退避が残っていれば、クエリ込みの退避を使う。
        const target =
          returnToFromPortal &&
          !(saved && pathWithoutQuery(saved) === returnToFromPortal)
            ? returnToFromPortal
            : saved;
        const current = `${requestUrl.pathname}${requestUrl.search}`;
        const enteredOtherTenant =
          previousTenantId !== null &&
          exchangeTenantId !== null &&
          exchangeTenantId !== previousTenantId;
        if (hash.authCode && target && target !== current && !enteredOtherTenant) {
          throw redirect(target);
        }
      }

      return {
        ...whoamiResponse.me,
        currentTenantId: tenantId,
      };
    } catch (error) {
      // redirect() による throw は再スロー
      if (error instanceof Response) {
        throw error;
      }

      // その他のエラーは認証ポータルへリダイレクト
      console.error("Auth loader error:", error);
      if (hash.auto != null) {
        throwReauth("&reauth");
      }
      throwReauth();
    }
  };
}
